import React, { useEffect, useMemo, useState } from "react";
import {
  FiSearch,
  FiRefreshCw,
  FiPrinter,
  FiShoppingBag,
  FiCreditCard,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import adminAxios from "../../utils/adminAxios";

const STATUS_COLORS = {
  initiated: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
  authorized: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  paid: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  captured: "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200",
  verified:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  refunded: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  voided: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
  failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  unpaid: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
};

const SUCCESSFUL_STATUSES = ["paid", "captured", "verified"];

const Orders = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [printingId, setPrintingId] = useState(null);
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const STATUS_LABELS = useMemo(
    () => ({
      initiated: t("dashboard.orders.status.initiated"),
      authorized: t("dashboard.orders.status.authorized"),
      paid: t("dashboard.orders.status.paid"),
      captured: t("dashboard.orders.status.captured"),
      verified: t("dashboard.orders.status.verified"),
      refunded: t("dashboard.orders.status.refunded"),
      voided: t("dashboard.orders.status.voided"),
      failed: t("dashboard.orders.status.failed"),
    }),
    [t]
  );

  const fetchOrders = async (isBackground = false) => {
    try {
      if (isBackground) setRefreshing(true);
      else setLoading(true);
      const res = await adminAxios.get("order/get-all-orders", {
        params: {
          name: name || undefined,
          number: number ? "+966" + number : undefined,
        },
      });
      setOrders(res.data.data || []);
    } catch (err) {
      toast.error(
        err.response?.data?.message || t("dashboard.orders.toast.fetchError")
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOrders(orders.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, number]);

  const stats = useMemo(() => {
    const total = orders.length;
    const paid = orders.filter((o) =>
      SUCCESSFUL_STATUSES.includes(o.paymentStatus)
    ).length;
    const revenue = orders
      .filter((o) => SUCCESSFUL_STATUSES.includes(o.paymentStatus))
      .reduce((sum, o) => sum + (o.total || 0), 0);
    return { total, paid, unpaid: total - paid, revenue };
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (statusFilter === "all") return orders;
    if (statusFilter === "unpaid") return orders.filter((o) => !o.paymentStatus);
    return orders.filter((o) => o.paymentStatus === statusFilter);
  }, [orders, statusFilter]);

  const handlePrint = async (id, paymentStatus) => {
    if (!paymentStatus) {
      toast.error(t("dashboard.orders.toast.printUnpaidError"));
      return;
    }
    try {
      setPrintingId(id);
      const response = await adminAxios.get("order/print-way-bill", {
        params: { id },
      });

      const base64 = response.data.data;
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `waybill-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        error.response?.data?.message || t("dashboard.orders.toast.printError")
      );
    } finally {
      setPrintingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div
      className={`space-y-6 transition-opacity duration-300 ${
        refreshing ? "opacity-60" : "opacity-100"
      }`}
    >
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("dashboard.orders.title")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t("dashboard.orders.subtitle")}
          </p>
        </div>
        <button
          onClick={() => fetchOrders(true)}
          disabled={refreshing}
          className="mt-4 lg:mt-0 flex items-center justify-center space-x-2 rtl:space-x-reverse px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
        >
          <FiRefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          <span>{t("dashboard.orders.refresh")}</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="text-3xl font-bold">{stats.total}</div>
          <div className="text-lg opacity-90">
            {t("dashboard.orders.stats.total")}
          </div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="text-3xl font-bold">
            {stats.revenue.toLocaleString("en-US")}{" "}
            {t("dashboard.orders.currency")}
          </div>
          <div className="text-lg opacity-90">
            {t("dashboard.orders.stats.revenue")}
          </div>
        </div>
        <div className="bg-gradient-dark rounded-2xl p-6 text-white shadow-lg">
          <div className="text-3xl font-bold">{stats.unpaid}</div>
          <div className="text-lg opacity-90">
            {t("dashboard.orders.stats.unpaid")}
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={t("dashboard.orders.search.namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full pl-10 pr-4 rtl:pl-4 rtl:pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={t("dashboard.orders.search.phonePlaceholder")}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            className="w-full pl-10 pr-4 rtl:pl-4 rtl:pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        >
          <option value="all">{t("dashboard.orders.filter.all")}</option>
          <option value="unpaid">{t("dashboard.orders.status.unpaid")}</option>
          {Object.keys(STATUS_LABELS).map((status) => (
            <option key={status} value={status}>
              {STATUS_LABELS[status]}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.orders.table.id")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.orders.table.customer")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.orders.table.location")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.orders.table.itemDesc")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.orders.table.pieces")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.orders.table.total")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.orders.table.paymentMethod")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.orders.table.paymentStatus")}
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.orders.table.waybill")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
              {filteredOrders.map((order) => (
                <tr
                  key={order.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-right">
                    #{order.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {order.cName}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {order.cMobile}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {order.cCity}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 max-w-[200px] truncate">
                      {order.cAddress}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-900 dark:text-white max-w-[200px] truncate">
                    {order.itemDesc}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                    {order.PCs}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white text-right">
                    {(order.total || 0).toLocaleString("en-US")}{" "}
                    {t("dashboard.orders.currency")}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1 text-sm text-gray-900 dark:text-white">
                      <FiCreditCard className="w-4 h-4 text-gray-400" />
                      {order.codAmt > 0
                        ? t("dashboard.orders.paymentMethod.cod")
                        : t("dashboard.orders.paymentMethod.card")}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span
                      className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        STATUS_COLORS[order.paymentStatus] ||
                        STATUS_COLORS.unpaid
                      }`}
                    >
                      {order.paymentStatus
                        ? STATUS_LABELS[order.paymentStatus] ||
                          order.paymentStatus
                        : t("dashboard.orders.status.unpaid")}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                    <button
                      onClick={() => handlePrint(order.id, order.paymentStatus)}
                      disabled={!order.paymentStatus || printingId === order.id}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors px-3 py-1 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      <FiPrinter className="w-4 h-4" />
                      {printingId === order.id
                        ? t("dashboard.orders.actions.printing")
                        : t("dashboard.orders.actions.print")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredOrders.length === 0 && (
          <div className="text-center py-12">
            <FiShoppingBag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t("dashboard.orders.empty.title")}
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {name || number || statusFilter !== "all"
                ? t("dashboard.orders.empty.filtered")
                : t("dashboard.orders.empty.noOrders")}
            </p>
          </div>
        )}
      </div>

      <div className="text-sm text-gray-600 dark:text-gray-400">
        {t("dashboard.orders.footer.count", { count: filteredOrders.length })}
      </div>
    </div>
  );
};

export default Orders;
